/**
 * One estimate: its lines, each priced two ways — the cheapest valid way to
 * buy it (the package comparison) and what the client pays for it (cost,
 * sell, profit, margin and markup together) — status, and converting a won
 * estimate into the project's contract value and its commitments. See
 * docs/estimating.md.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { api, apiErrorMessage, fmtAmount, formatPercent, rateFromPercent, unwrap } from "../api";
import { Button, Empty, ErrorNote, Field, Loading, Modal, Panel, Stat, input } from "../ui";
import { StatusPill } from "./Contracts";
import {
  PRICING_UNITS,
  PackageTable,
  type PricingUnit,
  UNIT_LABELS,
  useProductTypes,
  useWorkStages,
} from "./estimating";
import { ClientPicker } from "./shared";

/** A proposal in this state is dead paperwork — it shouldn't block, or even
 *  read as, "this estimate already has one out". */
const LIVE_PROPOSAL_STATUSES = new Set(["draft", "issued", "accepted", "approved"]);

const STATUS_OPTIONS = ["draft", "issued", "won", "lost", "void"] as const;

type SellBasis = "price_book" | "markup" | "margin" | "manual";

interface LineRow {
  key: number;
  productTypeId: string;
  description: string;
  stageIds: string[];
  measures: { mkey: number; unit: PricingUnit; quantity: string }[];
  sellBasis: SellBasis;
  sellPriceItemId: string;
  sellRatePercent: string;
  sellAmount: string;
}

export function EstimateDetail({
  slug,
  estimateId,
  currency,
  role,
  onBack,
  onOpenContract,
  onOpenProject,
}: {
  slug: string;
  estimateId: string;
  currency: string;
  role: string;
  onBack: () => void;
  onOpenContract: (id: string) => void;
  onOpenProject?: (id: string) => void;
}) {
  const qc = useQueryClient();
  const canManage = role === "manager" || role === "admin" || role === "owner";
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposing, setProposing] = useState(false);
  // Whether the proposal about to be built is a deliberate second one — set
  // only from the "Build another" action, never the default "Build proposal".
  const [buildingAnother, setBuildingAnother] = useState(false);

  const estimate = useQuery({
    queryKey: ["estimate", slug, estimateId],
    queryFn: async () =>
      unwrap(await api.api.c[":slug"].estimates[":id"].$get({ param: { slug, id: estimateId } })),
  });

  const stages = useWorkStages(slug);
  const productTypes = useProductTypes(slug);
  const sellLists = useQuery({
    queryKey: ["price-lists", slug, "sell", null],
    queryFn: async () =>
      unwrap(
        await api.api.c[":slug"].pricing["price-lists"].$get({
          param: { slug },
          query: { side: "sell" },
        }),
      ),
  });
  const sellItems = (sellLists.data?.priceLists ?? []).flatMap((l) => l.items);

  function refresh() {
    qc.invalidateQueries({ queryKey: ["estimate", slug, estimateId] });
  }

  const setStatus = useMutation({
    mutationFn: async (status: (typeof STATUS_OPTIONS)[number]) =>
      unwrap(
        await api.api.c[":slug"].estimates[":id"].status.$post({
          param: { slug, id: estimateId },
          json: { status },
        }),
      ),
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err, "Couldn't change status.")),
  });

  /**
   * A won estimate becomes real as a proposal the client signs. Each priced
   * line becomes its own part of the document — its own project once the
   * contract is approved — so a job quoted as a kitchen and a closet arrives
   * as two, the way it was priced.
   */
  const buildProposal = useMutation({
    mutationFn: async (body: { title: string; clientId: string }) =>
      unwrap(
        await api.api.c[":slug"].contracts.$post({
          param: { slug },
          json: { ...body, currency, estimateId, allowAdditionalProposal: buildingAnother },
        }),
      ),
    onSuccess: (res) => {
      setProposing(false);
      setBuildingAnother(false);
      refresh();
      onOpenContract((res as { contract: { id: string } }).contract.id);
    },
    onError: (err) => setError(apiErrorMessage(err, "Couldn't build a proposal from this.")),
  });

  const select = useMutation({
    mutationFn: async ({ lineId, priceItemIds }: { lineId: string; priceItemIds: string[] }) =>
      unwrap(
        await api.api.c[":slug"].estimates[":id"].lines[":lineId"].select.$post({
          param: { slug, id: estimateId, lineId },
          json: { priceItemIds },
        }),
      ),
    onSuccess: refresh,
    onError: (err) => setError(apiErrorMessage(err, "That package couldn't be chosen.")),
  });

  if (estimate.isPending) return <Loading />;
  if (estimate.isError) return <ErrorNote>{(estimate.error as Error).message}</ErrorNote>;

  const e = estimate.data.estimate;
  const lines = estimate.data.lines;
  const totals = estimate.data.totals;
  const proposals = estimate.data.proposals ?? [];
  const madeProjects = estimate.data.projects ?? [];
  // The most recent proposal still worth acting on — a declined or expired
  // one shouldn't stop a new one from being built without an override.
  const liveProposal = proposals.find((p) => LIVE_PROPOSAL_STATUSES.has(p.status));

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={onBack}>
          ← Estimates
        </Button>
      </div>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Panel
        title={e.name}
        action={
          canManage && (
            <div className="flex items-center gap-2">
              <select
                className={input}
                value={e.status}
                onChange={(ev) =>
                  setStatus.mutate(ev.target.value as (typeof STATUS_OPTIONS)[number])
                }
                disabled={setStatus.isPending}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              {liveProposal ? (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => onOpenContract(liveProposal.id)}
                    title="Open the proposal already built from this estimate"
                  >
                    Open proposal {liveProposal.number}
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setError(null);
                      setBuildingAnother(true);
                      setProposing(true);
                    }}
                    title="Build a second, separate proposal from these lines"
                  >
                    Build another
                  </Button>
                </>
              ) : (
                <Button
                  variant="ghost"
                  onClick={() => {
                    setError(null);
                    setBuildingAnother(false);
                    setProposing(true);
                  }}
                  title="Build a client-facing proposal from these lines"
                >
                  Build proposal
                </Button>
              )}
            </div>
          )
        }
      >
        {(proposals.length > 0 || madeProjects.length > 0) && (
          <p className="mb-4 text-sm text-neutral-500">
            {proposals.length > 0 && (
              <>
                {proposals.length === 1 ? "Proposal " : "Proposals "}
                {proposals.map((p, i) => (
                  <span key={p.id}>
                    {i > 0 && ", "}
                    <button
                      type="button"
                      onClick={() => onOpenContract(p.id)}
                      className="underline underline-offset-2 hover:text-neutral-900"
                    >
                      {p.number}
                    </button>{" "}
                    <StatusPill status={p.status} />
                  </span>
                ))}
              </>
            )}
            {madeProjects.length > 0 && (
              <>
                {proposals.length > 0 && " · "}
                {madeProjects.length === 1 ? "Created project " : "Created projects "}
                {madeProjects.map((p, i) => (
                  <span key={p.id}>
                    {i > 0 && ", "}
                    {onOpenProject ? (
                      <button
                        type="button"
                        onClick={() => onOpenProject(p.id)}
                        className="underline underline-offset-2 hover:text-neutral-900"
                      >
                        {p.name}
                      </button>
                    ) : (
                      p.name
                    )}
                  </span>
                ))}
              </>
            )}
          </p>
        )}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat label="Cost" value={fmtAmount(totals.cost, e.currency)} />
          <Stat label="Sell" value={fmtAmount(totals.sell, e.currency)} />
          <Stat label="Profit" value={fmtAmount(totals.profit, e.currency)} />
          <Stat label="Margin" value={formatPercent(totals.marginRate)} />
        </div>
      </Panel>

      <Panel
        title="Lines"
        action={
          canManage && (
            <Button onClick={() => setEditing(!editing)}>
              {editing ? "Cancel" : "Edit lines"}
            </Button>
          )
        }
      >
        {editing ? (
          <LinesEditor
            slug={slug}
            estimateId={estimateId}
            initial={lines}
            stages={stages.data?.stages ?? []}
            productTypes={productTypes.data?.productTypes ?? []}
            sellItems={sellItems}
            onSaved={() => {
              setEditing(false);
              refresh();
            }}
          />
        ) : lines.length === 0 ? (
          <Empty title="No lines yet">Add a line for each product this job needs priced.</Empty>
        ) : (
          <div className="flex flex-col gap-5">
            {lines.map((line) => {
              const productType = productTypes.data?.productTypes.find(
                (t) => t.id === line.productTypeId,
              );
              const selectedIds = line.selections.map((s) => s.priceItemId);
              return (
                <div
                  key={line.id}
                  className="rounded-xl border border-neutral-200 bg-white p-4 shadow-sm"
                >
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <div>
                      <span className="font-medium text-neutral-900">
                        {productType?.name ?? "—"}
                      </span>
                      {line.description && (
                        <span className="ml-2 text-sm text-neutral-500">{line.description}</span>
                      )}
                    </div>
                    <div className="flex gap-4 text-sm">
                      {line.resolvedSell ? (
                        <>
                          <span className="text-neutral-500">
                            Cost {fmtAmount(line.resolvedSell.cost, e.currency)}
                          </span>
                          <span className="font-medium">
                            Sell {fmtAmount(line.resolvedSell.sell, e.currency)}
                          </span>
                          <span
                            className={
                              line.resolvedSell.profit.startsWith("-")
                                ? "text-red-600"
                                : "text-emerald-700"
                            }
                          >
                            Profit {fmtAmount(line.resolvedSell.profit, e.currency)} ·{" "}
                            {formatPercent(line.resolvedSell.marginRate)} margin /{" "}
                            {formatPercent(line.resolvedSell.markupRate)} markup
                          </span>
                        </>
                      ) : (
                        <span className="text-amber-700">
                          Can't price this line's sell basis yet
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="mb-2 text-xs text-neutral-500">
                    {line.measures
                      .map((m) => `${m.quantity} ${UNIT_LABELS[m.unit as PricingUnit]}`)
                      .join(", ")}
                  </p>
                  <PackageTable
                    comparison={line.comparison}
                    currency={e.currency}
                    stages={stages.data?.stages ?? []}
                    selectedIds={selectedIds}
                    onChoose={
                      canManage
                        ? (priceItemIds) => select.mutate({ lineId: line.id, priceItemIds })
                        : undefined
                    }
                  />
                </div>
              );
            })}
          </div>
        )}
      </Panel>

      {proposing && (
        <ProposalModal
          slug={slug}
          defaultTitle={e.name}
          defaultClientId={e.clientId}
          lineCount={lines.length}
          pricedCount={lines.filter((l) => l.selections.length > 0).length}
          pending={buildProposal.isPending}
          existingProposal={buildingAnother ? liveProposal : undefined}
          onCancel={() => {
            setProposing(false);
            setBuildingAnother(false);
          }}
          onConfirm={(body) => buildProposal.mutate(body)}
        />
      )}
    </div>
  );
}

/**
 * What a proposal needs that an estimate doesn't: a client to address it to.
 *
 * An estimate is often started before anyone knows whose job it is — the
 * question is "what would this cost", not "what do we charge the Francos". A
 * proposal has to be addressed to somebody, so this asks at the point it
 * actually matters, and can create the client without leaving the estimate.
 */
function ProposalModal({
  slug,
  defaultTitle,
  defaultClientId,
  lineCount,
  pricedCount,
  pending,
  existingProposal,
  onCancel,
  onConfirm,
}: {
  slug: string;
  defaultTitle: string;
  defaultClientId: string | null;
  lineCount: number;
  pricedCount: number;
  pending: boolean;
  existingProposal?: { number: string; status: string } | undefined;
  onCancel: () => void;
  onConfirm: (body: { title: string; clientId: string }) => void;
}) {
  const [title, setTitle] = useState(defaultTitle);
  const [clientId, setClientId] = useState(defaultClientId ?? "");

  return (
    <Modal
      title={existingProposal ? "Build another proposal" : "Build a proposal"}
      onClose={onCancel}
    >
      <p className="text-sm text-neutral-600">
        Each priced line becomes its own part of the proposal — and its own project once the client
        signs. You can merge or rename them in the document.
      </p>
      {existingProposal && (
        <p className="mt-2 text-sm text-amber-700">
          This estimate already has proposal {existingProposal.number} ({existingProposal.status}).
          This builds a separate, second one from the same lines — make sure that's deliberate.
        </p>
      )}
      {pricedCount === 0 ? (
        <ErrorNote>
          None of these lines has a contractor price chosen yet. Choose one on each line first — a
          proposal is built from what the job will actually cost.
        </ErrorNote>
      ) : (
        pricedCount < lineCount && (
          <p className="mt-2 text-sm text-amber-700">
            {lineCount - pricedCount} of {lineCount} lines have no price chosen yet, so they won't
            carry a contractor into the proposal.
          </p>
        )
      )}
      <div className="mt-4 grid gap-3">
        <Field label="Proposal title">
          <input className={input} value={title} onChange={(ev) => setTitle(ev.target.value)} />
        </Field>
        <ClientPicker slug={slug} value={clientId} onChange={setClientId} required />
      </div>
      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm({ title: title.trim() || defaultTitle, clientId })}
          disabled={pending || !clientId || pricedCount === 0}
        >
          {pending ? "Building…" : "Build proposal"}
        </Button>
      </div>
    </Modal>
  );
}

function LinesEditor({
  slug,
  estimateId,
  initial,
  stages,
  productTypes,
  sellItems,
  onSaved,
}: {
  slug: string;
  estimateId: string;
  initial: readonly {
    productTypeId: string;
    description: string | null;
    stageIds: readonly string[];
    measures: readonly { unit: string; quantity: string }[];
    sellBasis: string;
    sellPriceItemId: string | null;
    sellRate: string | null;
    sellAmount: string | null;
  }[];
  stages: readonly { id: string; name: string }[];
  productTypes: readonly { id: string; name: string; stageIds: readonly string[] }[];
  sellItems: readonly {
    id: string;
    label: string;
    productTypeId: string;
    unit: string;
    unitPrice: string;
  }[];
  onSaved: () => void;
}) {
  const nextKey = useRef(0);
  const [rows, setRows] = useState<LineRow[]>(() =>
    (initial.length > 0 ? initial : []).map((l) => ({
      key: nextKey.current++,
      productTypeId: l.productTypeId,
      description: l.description ?? "",
      stageIds: [...l.stageIds],
      measures: l.measures.map((m) => ({
        mkey: nextKey.current++,
        unit: m.unit as PricingUnit,
        quantity: m.quantity,
      })),
      sellBasis: l.sellBasis as SellBasis,
      sellPriceItemId: l.sellPriceItemId ?? "",
      sellRatePercent: l.sellRate ? String(Number(l.sellRate) * 100) : "",
      sellAmount: l.sellAmount ?? "",
    })),
  );
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.api.c[":slug"].estimates[":id"].lines.$put({
          param: { slug, id: estimateId },
          json: {
            lines: rows.map((r) => ({
              productTypeId: r.productTypeId,
              description: r.description || undefined,
              stageIds: r.stageIds,
              measures: r.measures.filter((m) => m.quantity.trim() !== ""),
              sellBasis: r.sellBasis,
              ...(r.sellBasis === "price_book" ? { sellPriceItemId: r.sellPriceItemId } : {}),
              ...(r.sellBasis === "markup" || r.sellBasis === "margin"
                ? { sellRate: rateFromPercent(r.sellRatePercent) }
                : {}),
              ...(r.sellBasis === "manual" ? { sellAmount: r.sellAmount } : {}),
            })),
          },
        }),
      ),
    onSuccess: onSaved,
    onError: (err) => setError(apiErrorMessage(err, "Couldn't save these lines.")),
  });

  function update(key: number, patch: Partial<LineRow>) {
    setRows(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  }

  return (
    <div>
      {error && <ErrorNote>{error}</ErrorNote>}
      <div className="flex flex-col gap-4">
        {rows.map((r) => (
          <div key={r.key} className="rounded-lg border border-neutral-200 p-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <Field label="Product type">
                <select
                  className={input}
                  value={r.productTypeId}
                  onChange={(ev) => {
                    const pt = productTypes.find((t) => t.id === ev.target.value);
                    update(r.key, {
                      productTypeId: ev.target.value,
                      stageIds: pt ? [...pt.stageIds] : r.stageIds,
                    });
                  }}
                >
                  <option value="">— pick —</option>
                  {productTypes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Description" hint="Optional">
                <input
                  className={input}
                  value={r.description}
                  onChange={(ev) => update(r.key, { description: ev.target.value })}
                />
              </Field>
            </div>

            <Field label="Stages this line needs">
              <div className="flex flex-wrap gap-2 pt-1">
                {stages.map((s) => (
                  <label
                    key={s.id}
                    className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-2.5 py-1 text-sm"
                  >
                    <input
                      type="checkbox"
                      checked={r.stageIds.includes(s.id)}
                      onChange={(ev) =>
                        update(r.key, {
                          stageIds: ev.target.checked
                            ? [...r.stageIds, s.id]
                            : r.stageIds.filter((x) => x !== s.id),
                        })
                      }
                    />
                    {s.name}
                  </label>
                ))}
              </div>
            </Field>

            <div className="mt-2 flex flex-col gap-2">
              {r.measures.map((m) => (
                <div key={m.mkey} className="flex items-end gap-2">
                  <Field label="Unit">
                    <select
                      className={input}
                      value={m.unit}
                      onChange={(ev) =>
                        update(r.key, {
                          measures: r.measures.map((x) =>
                            x.mkey === m.mkey ? { ...x, unit: ev.target.value as PricingUnit } : x,
                          ),
                        })
                      }
                    >
                      {PRICING_UNITS.map((u) => (
                        <option key={u} value={u}>
                          {UNIT_LABELS[u]}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Quantity">
                    <input
                      className={input}
                      value={m.quantity}
                      onChange={(ev) =>
                        update(r.key, {
                          measures: r.measures.map((x) =>
                            x.mkey === m.mkey ? { ...x, quantity: ev.target.value } : x,
                          ),
                        })
                      }
                    />
                  </Field>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      update(r.key, { measures: r.measures.filter((x) => x.mkey !== m.mkey) })
                    }
                  >
                    Remove
                  </Button>
                </div>
              ))}
              <Button
                variant="ghost"
                onClick={() =>
                  update(r.key, {
                    measures: [
                      ...r.measures,
                      { mkey: nextKey.current++, unit: "inch", quantity: "" },
                    ],
                  })
                }
              >
                Add a measurement
              </Button>
            </div>

            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              <Field label="Priced to the client, off">
                <select
                  className={input}
                  value={r.sellBasis}
                  onChange={(ev) => update(r.key, { sellBasis: ev.target.value as SellBasis })}
                >
                  <option value="price_book">Our price book</option>
                  <option value="markup">Markup on cost</option>
                  <option value="margin">Target margin</option>
                  <option value="manual">A typed amount</option>
                </select>
              </Field>
              {r.sellBasis === "price_book" && (
                <Field label="Price">
                  <select
                    className={input}
                    value={r.sellPriceItemId}
                    onChange={(ev) => update(r.key, { sellPriceItemId: ev.target.value })}
                  >
                    <option value="">— pick —</option>
                    {sellItems
                      .filter((it) => !r.productTypeId || it.productTypeId === r.productTypeId)
                      .map((it) => (
                        <option key={it.id} value={it.id}>
                          {it.label} ({it.unitPrice}/{UNIT_LABELS[it.unit as PricingUnit]})
                        </option>
                      ))}
                  </select>
                </Field>
              )}
              {(r.sellBasis === "markup" || r.sellBasis === "margin") && (
                <Field label={r.sellBasis === "markup" ? "Markup %" : "Margin %"}>
                  <input
                    className={input}
                    value={r.sellRatePercent}
                    onChange={(ev) => update(r.key, { sellRatePercent: ev.target.value })}
                    placeholder="e.g. 40"
                  />
                </Field>
              )}
              {r.sellBasis === "manual" && (
                <Field label="Amount">
                  <input
                    className={input}
                    value={r.sellAmount}
                    onChange={(ev) => update(r.key, { sellAmount: ev.target.value })}
                  />
                </Field>
              )}
            </div>

            <div className="mt-3">
              <Button variant="ghost" onClick={() => setRows(rows.filter((x) => x.key !== r.key))}>
                Remove line
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="mt-4 flex gap-2">
        <Button
          variant="ghost"
          onClick={() =>
            setRows([
              ...rows,
              {
                key: nextKey.current++,
                productTypeId: "",
                description: "",
                stageIds: [],
                measures: [{ mkey: nextKey.current++, unit: "inch", quantity: "" }],
                sellBasis: "price_book",
                sellPriceItemId: "",
                sellRatePercent: "",
                sellAmount: "",
              },
            ])
          }
        >
          Add line
        </Button>
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? "Saving…" : "Save lines"}
        </Button>
      </div>
    </div>
  );
}
